// ============= Imports =============
import React, { useState, useEffect } from 'react';
import { Spacer } from '@tw/tw-component-library';
import { Button } from '@tw/tw-component-library/common-wt';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import LogoImage from '../../../assets/Winston_Taylor_Primary_Wordmark_Paper_White_WT_Purple_RGB.png';
import {
  HeaderContainer,
  LayoutWrapper,
  LoginContainer,
  Logo,
  LogoVersionWrapper,
  MainContent,
  VersionText,
  WelcomeText,
} from './HeaderLayout.styles';
import { useNavigate } from 'react-router-dom';
import packageJson from '../../../../package.json';
import { DynamicVersionModal } from '../../common/DynamicVersionModal';

// ============= Types =============
interface HeaderLayoutProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
  activeAccount: AccountInfo | null;
  login: () => Promise<void> | void;
  logout: () => Promise<void> | void;
}

// ============= Helper Functions =============
const formatUserName = (name: string): string => {
  return name.split(', ').reverse().join(' ');
};

// Throttle function to limit scroll event frequency
const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;
  return function (this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// ============= Component =============
export const HeaderLayout: React.FC<HeaderLayoutProps> = ({
  children,
  activeAccount,
  login,
  logout,
}) => {
  const navigate = useNavigate();
  const [isCompact, setIsCompact] = useState(false);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);

  // Detect scroll position to enable compact mode with throttling and hysteresis
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      // Hysteresis: different thresholds for entering/exiting compact mode
      // Enter compact mode at 60px, exit at 40px to prevent flickering
      setIsCompact(isCompact ? scrollTop > 40 : scrollTop > 60);
    };

    const throttledHandleScroll = throttle(handleScroll, 100); // Throttle to 100ms
    window.addEventListener('scroll', throttledHandleScroll);
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [isCompact]);
  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleVersionClick = () => {
    setIsVersionModalOpen(true);
  };

  const handleVersionModalClose = () => {
    setIsVersionModalOpen(false);
  };

  return (
    <LayoutWrapper>
      <HeaderContainer $isCompact={isCompact}>
        <LogoVersionWrapper $isCompact={isCompact}>
          <Logo
            src={LogoImage}
            alt="Taylor Wessing Logo"
            width={200}
            height={50}
            onClick={() => navigate('/')}
            $isCompact={isCompact}
          />
          <VersionText
            onClick={handleVersionClick}
            title="Click to view version history"
          >
            v{packageJson.version}
          </VersionText>
        </LogoVersionWrapper>
        <AuthenticatedTemplate>
          <LoginContainer $isCompact={isCompact}>
            <WelcomeText>
              Welcome,{' '}
              {activeAccount?.name
                ? formatUserName(activeAccount.name)
                : 'User'}
            </WelcomeText>
            <Spacer />
            <Button
              variant="primary"
              onDark
              onClick={handleLogout}
              aria-label="Logout from application"
            >
              Logout
            </Button>
          </LoginContainer>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <Button
            variant="primary"
            onDark
            onClick={handleLogin}
            aria-label="Login to application"
          >
            Login
          </Button>
        </UnauthenticatedTemplate>
      </HeaderContainer>

      <MainContent>{children}</MainContent>

      <DynamicVersionModal
        isOpen={isVersionModalOpen}
        onClose={handleVersionModalClose}
      />
    </LayoutWrapper>
  );
};

// ============= PropTypes =============
HeaderLayout.displayName = 'HeaderLayout';
